/**
 * Data Privacy and PII Protection Utilities
 * Implements GDPR compliance and data masking features
 */

// PII patterns for detection and masking
const PII_PATTERNS = {
  email: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
  phone: /\b(?:\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})\b/g,
  ssn: /\b\d{3}-?\d{2}-?\d{4}\b/g,
  creditCard: /\b(?:\d{4}[-\s]?){3}\d{4}\b/g,
  ipAddress: /\b(?:[0-9]{1,3}\.){3}[0-9]{1,3}\b/g,
  // Common name patterns (basic detection)
  firstName: /\b[A-Z][a-z]{2,15}\s(?=\b[A-Z][a-z]{2,15}\b)/g,
  // Account numbers or IDs
  accountNumber: /\b(?:account|acct|id)[\s:]*#?(\d{6,})/gi
};

const MASKING_LEVELS = {
  NONE: 'none',
  PARTIAL: 'partial', 
  FULL: 'full',
  HASH: 'hash'
};

/**
 * Mask PII data based on type and level
 * @param {string} text - Text to mask
 * @param {string} type - Type of PII (email, phone, etc.)
 * @param {string} level - Masking level (partial, full, hash)
 * @returns {string} Masked text
 */
export const maskPII = (text, type, level = MASKING_LEVELS.PARTIAL) => {
  if (!text || level === MASKING_LEVELS.NONE) return text;

  switch (type) {
    case 'email':
      return maskEmail(text, level);
    case 'phone':
      return maskPhone(text, level);
    case 'ssn':
      return maskSSN(text, level);
    case 'creditCard':
      return maskCreditCard(text, level);
    case 'ipAddress':
      return maskIPAddress(text, level);
    default:
      return maskGeneric(text, level);
  }
};

/**
 * Mask email addresses
 */
const maskEmail = (email, level) => {
  switch (level) {
    case MASKING_LEVELS.FULL:
      return '[EMAIL_MASKED]';
    case MASKING_LEVELS.HASH:
      return `[EMAIL_${hashString(email)}]`;
    case MASKING_LEVELS.PARTIAL:
    default:
      const [local, domain] = email.split('@');
      if (!domain) return '[EMAIL_MASKED]';
      const maskedLocal = local.length > 2 
        ? local.substring(0, 2) + '*'.repeat(Math.max(local.length - 2, 1))
        : '*'.repeat(local.length);
      return `${maskedLocal}@${domain}`;
  }
};

/**
 * Mask phone numbers
 */
const maskPhone = (phone, level) => {
  switch (level) {
    case MASKING_LEVELS.FULL:
      return '[PHONE_MASKED]';
    case MASKING_LEVELS.HASH:
      return `[PHONE_${hashString(phone)}]`;
    case MASKING_LEVELS.PARTIAL:
    default:
      const digits = phone.replace(/\D/g, '');
      if (digits.length === 10) {
        return `(${digits.substring(0, 3)}) ***-****`;
      } else if (digits.length === 11) {
        return `+${digits.substring(0, 1)} (${digits.substring(1, 4)}) ***-****`;
      }
      return '[PHONE_MASKED]';
  }
};

/**
 * Mask SSN
 */
const maskSSN = (ssn, level) => {
  switch (level) {
    case MASKING_LEVELS.FULL:
      return '[SSN_MASKED]';
    case MASKING_LEVELS.HASH:
      return `[SSN_${hashString(ssn)}]`;
    case MASKING_LEVELS.PARTIAL:
    default:
      return 'XXX-XX-' + ssn.slice(-4);
  }
};

/**
 * Mask credit card numbers
 */
const maskCreditCard = (cardNumber, level) => {
  switch (level) {
    case MASKING_LEVELS.FULL:
      return '[CARD_MASKED]';
    case MASKING_LEVELS.HASH:
      return `[CARD_${hashString(cardNumber)}]`;
    case MASKING_LEVELS.PARTIAL:
    default:
      const digits = cardNumber.replace(/\D/g, '');
      return '**** **** **** ' + digits.slice(-4);
  }
};

/**
 * Mask IP addresses
 */
const maskIPAddress = (ip, level) => {
  switch (level) {
    case MASKING_LEVELS.FULL:
      return '[IP_MASKED]';
    case MASKING_LEVELS.HASH:
      return `[IP_${hashString(ip)}]`;
    case MASKING_LEVELS.PARTIAL:
    default:
      const parts = ip.split('.');
      if (parts.length === 4) {
        return `${parts[0]}.${parts[1]}.XXX.XXX`;
      }
      return '[IP_MASKED]';
  }
};

/**
 * Generic masking for unknown PII types
 */
const maskGeneric = (text, level) => {
  switch (level) {
    case MASKING_LEVELS.FULL:
      return '[PII_MASKED]';
    case MASKING_LEVELS.HASH:
      return `[PII_${hashString(text)}]`;
    case MASKING_LEVELS.PARTIAL:
    default:
      if (text.length <= 4) {
        return '*'.repeat(text.length);
      }
      return text.substring(0, 2) + '*'.repeat(text.length - 4) + text.substring(text.length - 2);
  }
};

/**
 * Automatically detect and mask PII in text
 * @param {string} text - Text to scan and mask
 * @param {Object} options - Masking options
 * @returns {string} Text with PII masked
 */
export const maskAllPII = (text, options = {}) => {
  const {
    maskingLevel = MASKING_LEVELS.PARTIAL,
    enabledPatterns = Object.keys(PII_PATTERNS),
    // eslint-disable-next-line no-unused-vars
    preserveStructure = true
  } = options;

  if (!text) return text;

  let maskedText = text;

  enabledPatterns.forEach(patternType => {
    if (PII_PATTERNS[patternType]) {
      maskedText = maskedText.replace(PII_PATTERNS[patternType], (match) => {
        return maskPII(match, patternType, maskingLevel);
      });
    }
  });

  return maskedText;
};

/**
 * Check if text contains PII
 * @param {string} text - Text to check
 * @returns {Object} Analysis of PII found
 */
export const detectPII = (text) => {
  if (!text) return { hasPII: false, types: [] };

  const detected = [];
  
  Object.entries(PII_PATTERNS).forEach(([type, pattern]) => {
    const matches = text.match(pattern);
    if (matches && matches.length > 0) {
      detected.push({
        type,
        count: matches.length,
        examples: matches.slice(0, 3) // First 3 examples
      });
    }
  });

  return {
    hasPII: detected.length > 0,
    types: detected
  };
};

/**
 * Simple hash function for generating consistent identifiers
 */
const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(16).substring(0, 8);
};

/**
 * GDPR compliance utilities
 */
export const GDPR = {
  /**
   * Check if user has provided consent for data processing
   */
  hasConsent: (userId, purposeType = 'audit_logs') => {
    try {
      const consent = localStorage.getItem(`gdpr_consent_${userId}_${purposeType}`);
      if (!consent) return false;
      
      const consentData = JSON.parse(consent);
      const expiryDate = new Date(consentData.expiry);
      
      return consentData.granted && expiryDate > new Date();
    } catch (error) {
      console.error('Error checking GDPR consent:', error);
      return false;
    }
  },

  /**
   * Record user consent
   */
  recordConsent: (userId, purposeType = 'audit_logs', granted = true, expiryDays = 365) => {
    try {
      const expiry = new Date();
      expiry.setDate(expiry.getDate() + expiryDays);
      
      const consentData = {
        granted,
        timestamp: new Date().toISOString(),
        expiry: expiry.toISOString(),
        purposeType
      };
      
      localStorage.setItem(`gdpr_consent_${userId}_${purposeType}`, JSON.stringify(consentData));
      return true;
    } catch (error) {
      console.error('Error recording GDPR consent:', error);
      return false;
    }
  },

  /**
   * Remove user consent
   */
  revokeConsent: (userId, purposeType = 'audit_logs') => {
    try {
      localStorage.removeItem(`gdpr_consent_${userId}_${purposeType}`);
      return true;
    } catch (error) {
      console.error('Error revoking GDPR consent:', error);
      return false;
    }
  },

  /**
   * Get data retention period in days based on data type
   */
  getRetentionPeriod: (dataType) => {
    const retentionPolicies = {
      'audit_logs': 2555, // 7 years
      'user_activity': 1095, // 3 years
      'security_events': 2555, // 7 years
      'system_logs': 365, // 1 year
      'default': 365
    };
    
    return retentionPolicies[dataType] || retentionPolicies.default;
  }
};

/**
 * Data retention utilities
 */
export const dataRetention = {
  /**
   * Check if log entry should be retained based on retention policy
   */
  shouldRetain: (logEntry, retentionPeriodDays) => {
    if (!logEntry.timestamp) return false;
    
    const logDate = new Date(logEntry.timestamp);
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - retentionPeriodDays);
    
    return logDate >= cutoffDate;
  },

  /**
   * Filter logs based on retention policy
   */
  filterByRetention: (logs, dataType = 'audit_logs') => {
    const retentionDays = GDPR.getRetentionPeriod(dataType);
    return logs.filter(log => dataRetention.shouldRetain(log, retentionDays));
  },

  /**
   * Get logs that are eligible for deletion
   */
  getExpiredLogs: (logs, dataType = 'audit_logs') => {
    const retentionDays = GDPR.getRetentionPeriod(dataType);
    return logs.filter(log => !dataRetention.shouldRetain(log, retentionDays));
  }
};

export { MASKING_LEVELS, PII_PATTERNS };
